import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type {
  CostItemsResponse,
  CostItemOptions,
  WorkOrderCostItem,
  CostItemCreate,
  CostItemUpdate,
} from '@/types/costItems'

export const useCostItemsForWorkOrder = (workOrderId: number | undefined) => {
  return useQuery<CostItemsResponse>({
    queryKey: ['cost-items', workOrderId],
    queryFn: async () => {
      const res = await axiosInstance.get<CostItemsResponse>(
        API_ENDPOINTS.WORK_ORDER_COST_ITEMS(workOrderId!)
      )
      return res.data
    },
    enabled: !!workOrderId,
  })
}

export const useCostItemOptions = () => {
  return useQuery<CostItemOptions>({
    queryKey: ['cost-item-options'],
    queryFn: async () => {
      const res = await axiosInstance.get<CostItemOptions>(API_ENDPOINTS.COST_ITEMS_OPTIONS)
      return res.data
    },
    staleTime: Infinity,
  })
}

export const useCreateCostItem = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CostItemCreate) => {
      const res = await axiosInstance.post<WorkOrderCostItem>(
        API_ENDPOINTS.WORK_ORDER_COST_ITEMS(workOrderId),
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-items', workOrderId] })
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? 'Errore nella creazione della riga di costo'
      )
    },
  })
}

export const useUpdateCostItem = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CostItemUpdate }) => {
      const res = await axiosInstance.patch<WorkOrderCostItem>(
        API_ENDPOINTS.COST_ITEM(id),
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-items', workOrderId] })
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? 'Errore nella modifica della riga di costo'
      )
    },
  })
}

export const useDeleteCostItem = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(API_ENDPOINTS.COST_ITEM(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-items', workOrderId] })
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? "Errore nell'eliminazione della riga di costo"
      )
    },
  })
}
